import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useMediaQuery,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import { capitalizeFirstLetter } from "../../components/reusableComponent/CapitalLetter";
import { useRecentTasksQuery } from "../../Services/activity/Recent";
import { formatDatee } from "../../components/reusableComponent/DateFormat";
import {
  getBadgeClasses,
  getIconClasses,
} from "../../components/reusableComponent/IconAndBadgeClasses";
import ReactModal from "react-modal";
import RecentTaskFilterModal from "./TaskFilterModal";
import {
  activityPaginationArrowBtnStyle,
  activityPaginationPageBtnStyle,
} from "./activityPaginationStyles";
import { ACTIVITY_PRIMARY, activityToolbarOutlinedSx } from "./activityUiTokens";

const customFilterModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "50px",
    margin: "auto",
    Overflow: "hidden",
    OverflowY: "auto",
  },
};

const SCROLL_POSITION_KEY = "recentTasksScrollPosition";
const LAST_VIEWED_TASK_KEY = "lastViewedRecent";

const RecentTask = ({ task_source }: { task_source: "email" | "ticket" }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [pageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(() => {
    const savedPage = sessionStorage.getItem("recentTasksCurrentPage");
    return savedPage ? Number(savedPage) : 1;
  });
  const [fetchData, setFetchData] = useState<boolean>(false);
  const [isFilterModalOpen, setFilterIsModalOpen] = useState(false);
  const [isFilterOn, setIsFilterOn] = useState<boolean>(false);
  const isSmallScreen = useMediaQuery("(max-width:575px)");

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRestoreCleanupRef = useRef<(() => void) | null>(null);

  const [selectedFilters, setSelectedFilters] = useState<any>({
    id: "",
    email_id: "",
    assignee_username: "",
    created_date: "",
    channel: "",
    disposition: "",
  });

  const [selectedFilters1, setSelectedFilters1] = useState<any>(() => {
    const sessionData = sessionStorage.getItem("selectedFilters");
    return sessionData
      ? JSON.parse(sessionData)
      : {
          id: "",
          email_id: "",
          assignee_username: "",
          created_date: "",
          channel: "",
          disposition: "",
        };
  });

  const { data: RecentTaskData } = useRecentTasksQuery({
    currentPage,
    selectedFilters1,
    isFilterOn,
    enabled: fetchData,
    task_source,
  });

  const rows =
    RecentTaskData?.results?.map((ticket: any) => ({
      id: ticket?.id,
      created: ticket?.created,
      disposition_name: ticket?.disposition_name,
      current_assignee: ticket?.current_assignee || "Unassigned",
      email_id: ticket?.email_id,
      channel: ticket?.channel,
      task_source: ticket?.task_source,
      non_actionable_marked_by: ticket?.non_actionable_marked_by || null,
      non_actionable_marked_at: ticket?.non_actionable_marked_at || null,
      non_actionable_marked_method:
        ticket?.non_actionable_marked_method || null,
    })) || [];

  const totalPages = Math.ceil(RecentTaskData?.count / pageSize);

  useEffect(() => {
    setFetchData(true);
  }, []);

  useEffect(() => {
    if (RecentTaskData) {
      setLoading(false);
    }
  }, [RecentTaskData]);

  useEffect(() => {
    const canRestore = !loading && rows.length > 0;
    if (canRestore) {
      const timer = setTimeout(() => {
        const cleanup = restoreScrollPosition();
        if (typeof cleanup === "function") {
          scrollRestoreCleanupRef.current = cleanup;
        }
      }, 50);
      return () => {
        clearTimeout(timer);
        if (scrollRestoreCleanupRef.current) {
          scrollRestoreCleanupRef.current();
          scrollRestoreCleanupRef.current = null;
        }
      };
    }
  }, [loading, rows]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const scrollableContainer = scrollContainerRef.current;
      if (scrollableContainer) {
        sessionStorage.setItem(
          SCROLL_POSITION_KEY,
          String(scrollableContainer.scrollTop),
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (
      selectedFilters1 &&
      Object.values(selectedFilters1).some((value) => value !== "")
    ) {
      setIsFilterOn(true);
    } else {
      setIsFilterOn(false);
    }
  }, [selectedFilters1]);

  const openFilterModal = () => setFilterIsModalOpen(true);
  const closeFilterModal = () => setFilterIsModalOpen(false);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => {
        const newPage = prevPage + 1;
        sessionStorage.setItem("recentTasksCurrentPage", newPage.toString());
        clearScrollPosition();
        return newPage;
      });
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => {
        const newPage = prevPage - 1;
        sessionStorage.setItem("recentTasksCurrentPage", newPage.toString());
        clearScrollPosition();
        return newPage;
      });
    }
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(() => {
      sessionStorage.setItem("recentTasksCurrentPage", pageNumber.toString());
      clearScrollPosition();
      return pageNumber;
    });
  };

  const generatePaginationNumbers = () => {
    const numbers = [];
    const maxVisibleNumbers = 5;

    if (totalPages <= maxVisibleNumbers) {
      for (let i = 1; i <= totalPages; i++) numbers.push(i);
    } else {
      const middleIndex = Math.ceil(maxVisibleNumbers / 2);
      let startPage = currentPage - middleIndex + 1;
      let endPage = currentPage + middleIndex - 1;

      if (startPage < 1) {
        endPage = maxVisibleNumbers;
        startPage = 1;
      } else if (endPage > totalPages) {
        startPage = totalPages - maxVisibleNumbers + 1;
        endPage = totalPages;
      }

      for (let i = startPage; i <= endPage; i++) numbers.push(i);
    }

    return numbers;
  };

  const handleSubmit = (filterValues: any) => {
    setSelectedFilters1(filterValues);
  };

  const storeScrollPosition = (taskId: string) => {
    const scrollableContainer = scrollContainerRef.current;
    if (scrollableContainer) {
      sessionStorage.setItem(
        SCROLL_POSITION_KEY,
        String(scrollableContainer.scrollTop),
      );
    }
    sessionStorage.setItem(LAST_VIEWED_TASK_KEY, taskId);
  };

  const restoreScrollPosition = (): (() => void) => {
    const savedScrollPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
    if (!savedScrollPosition) return () => {};

    const targetScrollTop = parseInt(savedScrollPosition, 10);
    if (Number.isNaN(targetScrollTop) || targetScrollTop < 0) return () => {};

    const applyScroll = () => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTop = targetScrollTop;
    };

    const delays = [0, 50, 150, 300, 500, 800, 1200, 1800, 2500, 3500];
    const timeouts = delays.map((d) => setTimeout(applyScroll, d));
    requestAnimationFrame(() => {
      requestAnimationFrame(applyScroll);
      clearScrollPosition();
    });
    return () => {
      timeouts.forEach((t) => clearTimeout(t));
    };
  };

  const clearScrollPosition = () => {
    sessionStorage.removeItem(SCROLL_POSITION_KEY);
    sessionStorage.removeItem(LAST_VIEWED_TASK_KEY);
  };

  const stickyCellStyle = {
    position: "sticky" as const,
    top: 0,
    backgroundColor: "#f9fafb",
    zIndex: 10,
    fontWeight: 600,
    fontSize: isSmallScreen ? "13px" : "15px",
    padding: isSmallScreen ? "8px 4px" : "12px 16px",
    whiteSpace: "nowrap" as const,
    minWidth: isSmallScreen ? "80px" : "auto",
  };

  const stickyCheckboxCellStyle = {
    position: "sticky" as const,
    top: 0,
    backgroundColor: "#f9fafb",
    zIndex: 11,
    padding: isSmallScreen ? "8px 4px" : "16px",
  };

  const formatNonActionableMethod = (method: string | null): string => {
    if (!method) return "-";
    if (method === "manual") return "Manual";
    if (method === "automatic") return "Automatic";
    return method;
  };

  return (
    <>
      <div
        className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-5"
        style={{
          marginLeft: isSmallScreen ? "10px" : "30px",
          marginRight: isSmallScreen ? "-35px" : "",
          gap: isSmallScreen ? "10px" : "0",
        }}
      >
        <div className="d-flex align-items-center">
          <h2
            style={{
              marginLeft: "-28px",
              fontSize: isSmallScreen ? "16px" : "",
            }}
          >
            Recent
          </h2>
        </div>

        <div className="w-100 mw-100px ms-auto mx-12">
          <Button
            variant="outlined"
            className="fw-bold"
            onClick={openFilterModal}
            sx={{
              ...activityToolbarOutlinedSx,
              height: isSmallScreen ? 32 : 40,
              width: isSmallScreen ? 100 : 140,
              gap: "5px",
              alignItems: "center",
              display: "flex",
              fontSize: isSmallScreen ? "13px" : "14px",
            }}
          >
            <i className="bi bi-filter fs-2" style={{ color: ACTIVITY_PRIMARY }} />
            <span
              className="ms-auto me-2"
              style={{
                textTransform: "none",
                whiteSpace: "nowrap",
                fontSize: isSmallScreen ? "11px" : "14px",
                color: ACTIVITY_PRIMARY,
              }}
            >
              Search filter
            </span>
          </Button>
        </div>

        <ReactModal
          isOpen={isFilterModalOpen}
          onRequestClose={closeFilterModal}
          style={customFilterModalStyles}
          contentLabel="Ticket Filter Modal"
          shouldCloseOnOverlayClick={false}
        >
          <RecentTaskFilterModal
            closeFilterModal={closeFilterModal}
            selectedFilterAndValue={selectedFilters}
            setSelectedFilterAndValue={setSelectedFilters}
            handleSubmit={handleSubmit}
          />
        </ReactModal>
      </div>

      <TableContainer
        component={Paper}
        ref={scrollContainerRef}
        style={{ maxHeight: 614.5, overflowY: "auto" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={stickyCheckboxCellStyle} />
              {task_source !== "email" && <TableCell sx={stickyCellStyle}>Ref id</TableCell>}
              <TableCell sx={stickyCellStyle}>Created date</TableCell>
              <TableCell sx={stickyCellStyle}>Disposition</TableCell>
              <TableCell sx={stickyCellStyle}>Current assignee</TableCell>
              <TableCell sx={stickyCellStyle}>Email id</TableCell>
              {task_source !== "email" && <TableCell sx={stickyCellStyle}>Channel</TableCell>}
              <TableCell sx={stickyCellStyle}>Non-Actionable marked by</TableCell>
              <TableCell sx={stickyCellStyle}>Non-Actionable marked at</TableCell>
              <TableCell sx={stickyCellStyle}>Non-Actionable type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={task_source === "email" ? 8 : 10} style={{ textAlign: "center" }}>
                  <LoadingSpinner />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={task_source === "email" ? 8 : 10}>
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    No data available
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row: any) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  component={Link}
                  to={
                    row.task_source === "email"
                      ? `/email/inbox/${row.id}`
                      : row.task_source === "ticket"
                      ? `/ticket/information/${row.id}`
                      : null
                  }
                  style={{ textDecoration: "none", color: "inherit" }}
                  onClick={() => storeScrollPosition(String(row.id))}
                >
                  <TableCell
                    sx={{ fontSize: isSmallScreen ? "12px" : "14px" }}
                  />
                  {task_source !== "email" && (
                    <TableCell
                      sx={{
                        fontSize: isSmallScreen ? "12px" : "14px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.id}
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontSize: isSmallScreen ? "12px" : "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.created}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: isSmallScreen ? "12px" : "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {capitalizeFirstLetter(row.disposition_name)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: isSmallScreen ? "12px" : "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {capitalizeFirstLetter(row.current_assignee)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: isSmallScreen ? "12px" : "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.email_id}
                  </TableCell>
                  {task_source !== "email" && (
                    <TableCell
                      title={row.channel}
                      style={{ paddingLeft: "28px" }}
                      sx={{
                        fontSize: isSmallScreen ? "12px" : "14px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        className={`${getBadgeClasses(row.channel)}`}
                        style={{
                          width: "35px",
                          height: "35px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                        }}
                      >
                        <i className={getIconClasses(row.channel)}></i>
                      </div>
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontSize: isSmallScreen ? "12px" : "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.non_actionable_marked_by || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: isSmallScreen ? "12px" : "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.non_actionable_marked_at
                      ? row.non_actionable_marked_at
                      : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: isSmallScreen ? "12px" : "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatNonActionableMethod(
                      row.non_actionable_marked_method,
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
        {!loading && !isFilterOn && (
          <div className="row mx-4 pt-2 mb-2 mt-3">
            <div className="col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start">
              <span>
                Page {currentPage} of {totalPages}
              </span>
            </div>
            <div className="col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end">
              <div className="dataTables_paginate paging_simple_numbers">
                <ul className="pagination">
                  <li
                    className={`paginate_button page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="btn page-link border-0"
                      style={activityPaginationArrowBtnStyle(
                        currentPage === 1,
                        isSmallScreen,
                      )}
                      onClick={prevPage}
                    >
                      <i className="previous"></i>
                    </button>
                  </li>

                  {generatePaginationNumbers().map((number) => (
                    <li
                      className={`paginate_button page-item ${
                        currentPage === number ? "active" : ""
                      }`}
                      key={number}
                    >
                      <button
                        type="button"
                        className="btn page-link border-0"
                        style={activityPaginationPageBtnStyle(
                          currentPage === number,
                          isSmallScreen,
                        )}
                        onClick={() => paginate(number)}
                      >
                        {number}
                      </button>
                    </li>
                  ))}

                  <li
                    className={`paginate_button page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="btn page-link border-0"
                      style={activityPaginationArrowBtnStyle(
                        currentPage === totalPages,
                        isSmallScreen,
                      )}
                      onClick={nextPage}
                    >
                      <i className="next"></i>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RecentTask;